import { useEffect, useCallback, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

let openModalCount = 0;
let previousBodyOverflow = "";
const modalStack: symbol[] = [];

type ModalSize = "sm" | "md" | "lg" | "xl";

const MODAL_WIDTHS: Record<ModalSize, string> = {
  sm: "400px",
  md: "480px",
  lg: "560px",
  xl: "600px",
};

import { Button } from "./Button";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  icon?: string;
  size?: ModalSize;
  children: ReactNode;
  footer?: ReactNode;
  dismissible?: boolean;
  showCloseButton?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  icon,
  size = "md",
  children,
  footer,
  dismissible = true,
  showCloseButton = true,
}: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const modalIdRef = useRef<symbol>(Symbol("modal"));
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  const isTopModal = useCallback(
    () => modalStack.at(-1) === modalIdRef.current,
    [],
  );

  const getFocusableElements = useCallback(() => {
    if (!panelRef.current) return [];

    return Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(
        [
          "a[href]",
          "button:not([disabled])",
          "textarea:not([disabled])",
          "input:not([disabled])",
          "select:not([disabled])",
          "[tabindex]:not([tabindex='-1'])",
        ].join(","),
      ),
    ).filter(
      (element) =>
        !element.hasAttribute("disabled") &&
        !element.getAttribute("aria-hidden") &&
        element.offsetParent !== null,
    );
  }, []);

  const focusInitialElement = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const autofocusElement = panel.querySelector<HTMLElement>(
      "[autofocus], [data-autofocus]",
    );
    const [firstFocusableElement] = getFocusableElements();
    const target = autofocusElement ?? firstFocusableElement ?? panel;

    target.focus();
  }, [getFocusableElements]);

  const handleKeyDown = useCallback(
    (e: globalThis.KeyboardEvent) => {
      if (!isTopModal()) return;

      if (e.key === "Escape" && dismissible) {
        onClose();
        return;
      }

      if (e.key !== "Tab") return;

      const focusableElements = getFocusableElements();

      if (!focusableElements.length) {
        e.preventDefault();
        panelRef.current?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
        return;
      }

      if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    },
    [dismissible, getFocusableElements, isTopModal, onClose],
  );

  useEffect(() => {
    if (!open) return;

    const modalId = modalIdRef.current;
    previouslyFocusedElementRef.current = document.activeElement as HTMLElement;

    if (openModalCount === 0) {
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    openModalCount += 1;
    modalStack.push(modalId);
    document.addEventListener("keydown", handleKeyDown);

    window.requestAnimationFrame(focusInitialElement);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      const stackIndex = modalStack.indexOf(modalId);
      if (stackIndex !== -1) {
        modalStack.splice(stackIndex, 1);
      }

      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) {
        document.body.style.overflow = previousBodyOverflow;
      }

      previouslyFocusedElementRef.current?.focus?.();
    };
  }, [open, focusInitialElement, handleKeyDown]);

  if (!open) return null;

  return createPortal(
    <div
      ref={overlayRef}
      onClick={(e) => {
        if (dismissible && isTopModal() && e.target === overlayRef.current) {
          onClose();
        }
      }}
      className="modal-overlay"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className="modal-panel"
        style={{
          maxWidth: MODAL_WIDTHS[size],
        }}
      >
        {/* Header */}
        <div className="modal-header shrink-0 px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex min-w-0 items-center gap-2.5">
            {icon && (
              <span
                className="material-symbols-outlined text-2xl text-secondary"
                aria-hidden
              >
                {icon}
              </span>
            )}
            <h3
              id={titleId}
              className="m-0 truncate text-base font-bold tracking-tight text-primary"
            >
              {title}
            </h3>
          </div>
          {showCloseButton && (
            <Button
              onClick={onClose}
              variant="icon"
              icon="close"
              aria-label={`Close ${title}`}
            />
          )}
        </div>

        {/* Body */}
        <div className="custom-scrollbar flex-1 overflow-y-auto p-4 sm:p-6">
          {description && (
            <div id={descriptionId} className="sr-only">
              {description}
            </div>
          )}
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="modal-footer shrink-0 flex-wrap px-4 py-3 sm:px-6 sm:py-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
